/**
 * 문서 폴더 시드 — 사용자가 만든 폴더 셋. 사람 id 는 data/staff.ts, 프로젝트 id 는 data/projects.ts 의
 * 것이다.
 *
 * 순서(position)와 켜짐 여부는 규칙이 뻔해서 아래에서 채운다 — 리터럴마다 적지 않는다.
 */
import type { DocumentField, DocumentOption, DocumentDetail, DocumentFolder } from "@/types";

type FieldSeed = Omit<DocumentField, "position" | "is_active" | "options"> & {
  options: Omit<DocumentOption, "position">[];
};
type FolderSeed = Omit<DocumentFolder, "fields"> & { fields: FieldSeed[] };

const FOLDERS: FolderSeed[] = [
  {
    id: "t-vendors",
    slug: "vendors",
    name: "설계업체",
    icon: "buildings",
    color: "#3182f6",
    description: "협력 설계업체와 담당자",
    position: 10,
    is_active: true,
    fields: [
      {
        id: "pr-field",
        name: "분야",
        value_type: "select",
        options: [
          { id: "o-structure", name: "구조", color: "blue" },
          { id: "o-mech", name: "기계", color: "green" },
          { id: "o-elec", name: "전기", color: "orange" },
          { id: "o-civil", name: "토목", color: "gray" },
        ],
      },
      { id: "pr-contact", name: "담당자", value_type: "text", options: [] },
      { id: "pr-phone", name: "연락처", value_type: "text", options: [] },
      { id: "pr-email", name: "이메일", value_type: "url", options: [] },
      { id: "pr-recent", name: "최근 협업", value_type: "project", options: [] },
      {
        id: "pr-grade",
        name: "평가",
        value_type: "select",
        options: [
          { id: "o-good", name: "우수", color: "green" },
          { id: "o-ok", name: "보통", color: "orange" },
          { id: "o-none", name: "미평가", color: "gray" },
        ],
      },
    ],
  },
  {
    id: "t-permits",
    slug: "permits",
    name: "인허가 업무",
    icon: "clipboard",
    color: "#fe9800",
    description: "접수와 보완, 처리 예정",
    position: 20,
    is_active: true,
    fields: [
      { id: "pr-project", name: "프로젝트", value_type: "project", options: [] },
      { id: "pr-office", name: "관청", value_type: "text", options: [] },
      { id: "pr-filed", name: "접수일", value_type: "date", options: [] },
      { id: "pr-due", name: "처리예정", value_type: "date", options: [] },
      {
        id: "pr-state",
        name: "상태",
        value_type: "select",
        options: [
          { id: "o-filed", name: "접수", color: "blue" },
          { id: "o-supp", name: "보완", color: "orange" },
          { id: "o-done", name: "완료", color: "green" },
          { id: "o-rejected", name: "반려", color: "red" },
        ],
      },
      { id: "pr-owner", name: "담당", value_type: "person", options: [] },
      { id: "pr-docs", name: "도서 (NAS)", value_type: "file", options: [] },
    ],
  },
  {
    id: "t-meetings",
    slug: "meetings",
    name: "협의내용",
    icon: "",
    color: "",
    description: "",
    position: 30,
    is_active: true,
    fields: [
      { id: "pr-date", name: "일자", value_type: "date", options: [] },
      { id: "pr-mproject", name: "프로젝트", value_type: "project", options: [] },
      { id: "pr-party", name: "상대", value_type: "text", options: [] },
      { id: "pr-who", name: "참석자", value_type: "person", options: [] },
      {
        id: "pr-tags",
        name: "주제",
        value_type: "multi_select",
        options: [
          { id: "o-finish", name: "마감재", color: "blue" },
          { id: "o-cost", name: "공사비", color: "orange" },
          { id: "o-parking", name: "주차", color: "gray" },
          { id: "o-structure2", name: "구조", color: "green" },
        ],
      },
      { id: "pr-followup", name: "후속조치 완료", value_type: "checkbox", options: [] },
    ],
  },
];

export const folders: DocumentFolder[] = FOLDERS.map((folder) => ({
  ...folder,
  fields: folder.fields.map((field, index) => ({
    ...field,
    position: (index + 1) * 10,
    is_active: true,
    options: field.options.map((option, order) => ({ ...option, position: (order + 1) * 10 })),
  })),
}));

const at = "2026-10-21T09:00:00+09:00";

type DocumentSeed = Omit<
  DocumentDetail,
  "position" | "project_id" | "created_by" | "created_at" | "deleted_at"
>;

const DOCS: DocumentSeed[] = [
  {
    id: "r-v1",
    folder_id: "t-vendors",
    title: "(주)한결구조",
    values: {
      "pr-field": "o-structure",
      "pr-contact": "정부장",
      "pr-phone": "02-000-0000",
      "pr-email": "mailto:jung@oo.co.kr",
      "pr-recent": ["p-2511"],
      "pr-grade": "o-ok",
    },
    body_md:
      "## 메모\n\n- 최근 회신이 늦다. 급한 건은 전화로 먼저 확인한다.\n- 지하층 구조 검토는 이 업체가 맡는다.",
    excerpt: "",
    version: 1,
    updated_at: at,
    updated_by: "u-owner",
  },
  {
    id: "r-v2",
    folder_id: "t-vendors",
    title: "대성기계설비",
    values: {
      "pr-field": "o-mech",
      "pr-contact": "한과장",
      "pr-phone": "02-111-1111",
      "pr-recent": ["p-2603"],
      "pr-grade": "o-good",
    },
    body_md: "소규모 현장도 빠르게 대응한다.",
    excerpt: "",
    version: 1,
    updated_at: at,
    updated_by: "u-owner",
  },
  {
    id: "r-v3",
    folder_id: "t-vendors",
    title: "동명전기",
    values: { "pr-field": "o-elec", "pr-contact": "서차장", "pr-grade": "o-good" },
    body_md: "",
    excerpt: "",
    version: 1,
    updated_at: at,
    updated_by: "u-staff",
  },
  {
    id: "r-p1",
    folder_id: "t-permits",
    title: "건축허가",
    values: {
      "pr-project": ["p-2603"],
      "pr-office": "마포구청 건축과",
      "pr-filed": "2026-07-14",
      "pr-due": "2026-08-22",
      "pr-state": "o-supp",
      "pr-owner": ["u-staff"],
      "pr-docs": "/projects/2603-망원수변산책로/허가/계획서_v3.pdf",
    },
    body_md:
      "## 보완 요구 (8/18 접수)\n\n주차 산정 근거를 보완하라는 요구.\n\n- 개정 시행령 적용 시점 확인\n- 주차 산정표 v2 작성",
    excerpt: "",
    version: 1,
    updated_at: at,
    updated_by: "u-staff",
  },
  {
    id: "r-p2",
    folder_id: "t-permits",
    title: "대수선허가",
    values: {
      "pr-project": ["p-2605"],
      "pr-office": "마포구청 건축과",
      "pr-filed": "2026-06-02",
      "pr-due": "2026-06-30",
      "pr-state": "o-done",
      "pr-owner": ["변경민"],
    },
    body_md: "",
    excerpt: "",
    version: 1,
    updated_at: at,
    updated_by: "변경민",
  },
  {
    id: "r-m1",
    folder_id: "t-meetings",
    title: "외벽 마감 변경 요청",
    values: {
      "pr-date": "2026-08-17",
      "pr-mproject": ["p-2511"],
      "pr-party": "건축주",
      "pr-who": ["u-staff", "변경민"],
      "pr-tags": ["o-finish", "o-cost"],
      "pr-followup": false,
    },
    body_md:
      "## 내용\n\n외벽 마감을 스터코에서 **점토벽돌**로 바꿔 달라는 요청. 공사비 증액 범위를 확인한 뒤 결정하기로 함.\n\n## 후속조치\n\n견적을 다시 뽑아 8/21까지 회신.",
    excerpt: "",
    version: 1,
    updated_at: at,
    updated_by: "u-staff",
  },
  {
    id: "r-m2",
    folder_id: "t-meetings",
    title: "주차 산정 사전 협의",
    values: {
      "pr-date": "2026-08-12",
      "pr-mproject": ["p-2603"],
      "pr-party": "마포구청 건축과",
      "pr-who": ["u-staff"],
      "pr-tags": ["o-parking"],
      "pr-followup": true,
    },
    body_md: "개정 시행령 적용 시점에 대한 해석을 확인했다.",
    excerpt: "",
    version: 1,
    updated_at: at,
    updated_by: "u-staff",
  },
];

/** 문서 — 만든 사람과 순서를 채운다. 프로젝트에 매단 문서는 아직 없다. */
export const documents: DocumentDetail[] = DOCS.map((row, index) => ({
  ...row,
  project_id: null,
  position: (index + 1) * 10,
  created_by: row.updated_by ?? "u-owner",
  created_at: at,
  deleted_at: null,
}));
